"use client";

import { forwardRef, type HTMLAttributes } from "react";

import { cx } from "./cx";
import styles from "./segmented-progress.module.css";

type Status = "neutral" | "success" | "warning" | "accent";
type Size = "sm" | "md" | "lg" | "xl";

interface SegmentedProgressProps extends HTMLAttributes<HTMLDivElement> {
  value?: number;
  segments?: number;
  status?: Status;
  size?: Size;
}

export const SegmentedProgress = forwardRef<
  HTMLDivElement,
  SegmentedProgressProps
>(
  (
    {
      className,
      value = 0,
      segments = 10,
      status = "neutral",
      size = "md",
      ...props
    },
    ref,
  ) => {
    const filledCount = Math.round((value / 100) * segments);

    return (
      <div
        ref={ref}
        className={cx(styles.track, styles[status], styles[size], className)}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
        {...props}
      >
        {Array.from({ length: segments }, (_, i) => (
          <div
            key={i}
            className={cx(styles.segment, i < filledCount && styles.filled)}
          />
        ))}
      </div>
    );
  },
);

SegmentedProgress.displayName = "SegmentedProgress";
